import { FC, useState } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import MainLayout from 'src/pages/private';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { useFormik } from 'formik';
import { rawMaterialCategoryInitialValues } from 'src/utils/initialValues.utils';
import { rawMaterialCategoryValidationSchema } from 'src/utils/validationScheme.utils';
import {
  apiUrls,
  rawMaterialCategoryDialogTitles,
  rawMaterialCategoryDialogSubtitles,
} from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import SearchBar from 'src/components/InputFields/Searchbar';
import RawMaterialCategoryGrid from 'src/components/Grids/RawMaterialCategoryGrid';
import CustomDialog from 'src/components/DialogBox/FormBox/CustomBox';
import TextField from 'src/components/InputFields/TextField';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import {
  PrimaryActionButton,
  ButtonIcon,
  OverflowContainer,
} from 'src/components/StyledComponents/StyledPageComponents';

const RawMaterialCategoryManagement: FC = () => {
  const theme = useTheme();
  const smScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [categoryAdded, setCategoryAdded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const { showSnackbar } = StyledSnackbar();

  const formik = useFormik({
    initialValues: rawMaterialCategoryInitialValues,
    validationSchema: rawMaterialCategoryValidationSchema,
    onSubmit: (values) => handleSubmit(values),
  });

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const addCategory = await axiosTokenInstance.post(
        apiUrls.ADD_RAW_MATERIAL_CATEGORY,
        {
          name: values.name,
        }
      );
      showSnackbar(addCategory.data.message, 'success');
      setCategoryAdded(!categoryAdded);
      handleClose();
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (newQuery: string) => {
    setSearchTerm(newQuery);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    formik.resetForm();
    setOpen(false);
  };

  return (
    <MainLayout
      title="Raw Material Category Management"
      searchBar={
        <SearchBar
          id="search"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search categories by name"
          width="300px"
        />
      }
      button={
        <PrimaryActionButton
          className="btn-primary"
          width="150px"
          onClick={handleClickOpen}
        >
          <ButtonIcon component="i" className="bx bx-plus 1" />
          New Category
        </PrimaryActionButton>
      }
    >
      <RawMaterialCategoryGrid state={categoryAdded} searchTerm={searchTerm} />
      <Box display="flex">
        <OverflowContainer display="flex" flexDirection="column">
          <CustomDialog
            height="340px"
            width="600px"
            open={open}
            handleClose={handleClose}
            title={rawMaterialCategoryDialogTitles.ADD}
            subtitle={rawMaterialCategoryDialogSubtitles.ADD}
            onSubmit={formik.handleSubmit}
            smScreen={smScreen}
            buttonDisabled={loading}
          >
            <Box display="flex" flexDirection="column">
              <Box display="flex" justifyContent="space-between" gap={2} mt={2}>
                <TextField
                  width="150%"
                  id="name"
                  label="Category Name"
                  formikProps={formik}
                />
              </Box>
            </Box>
          </CustomDialog>
        </OverflowContainer>
      </Box>
    </MainLayout>
  );
};

export default RawMaterialCategoryManagement;
